import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, User, BookOpen, CalendarCheck, FileText,
  Award, Wallet, LogOut, Menu, X, AlertCircle, PanelsTopLeft
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { collection, query, where, getDocs, doc, getDoc, limit } from "firebase/firestore";
import { db } from "../../firebase";
import BrandLogo from "../../components/BrandLogo";

export const StudentContext = React.createContext(null);

const navItems = [
  { to: "/student/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/student/profile", label: "My Profile", icon: User },
  { to: "/student/courses", label: "My Courses", icon: BookOpen },
  { to: "/student/attendance", label: "Attendance", icon: CalendarCheck },
  { to: "/student/assignments", label: "Assignments", icon: FileText },
  { to: "/student/certificate", label: "Certificate", icon: Award },
  { to: "/student/payments", label: "Payment History", icon: Wallet },
  { to: "/student/productivity", label: "Productivity Tools", icon: PanelsTopLeft },
];

const StudentLayout = () => {
  const { user, profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [student, setStudent] = useState(null);
  const [studentLoading, setStudentLoading] = useState(true);
  const [studentError, setStudentError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchStudent = async () => {
      if (!user?.uid) {
        setStudent(null);
        setStudentLoading(false);
        return;
      }

      setStudentLoading(true);
      setStudentError("");

      try {
        let resolvedStudent = null;

        if (profile?.studentId) {
          const directSnap = await getDoc(doc(db, "students", profile.studentId));
          if (directSnap.exists()) {
            resolvedStudent = { id: directSnap.id, ...directSnap.data() };
          }
        }

        if (!resolvedStudent) {
          const q = query(collection(db, "students"), where("uid", "==", user.uid), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            resolvedStudent = { id: snap.docs[0].id, ...snap.docs[0].data() };
          }
        }

        if (cancelled) return;

        setStudent(resolvedStudent);
        if (!resolvedStudent) {
          setStudentError(
            "No student record was found for this account. Please complete registration or contact support."
          );
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Student profile fetch failed:", e);
        setStudent(null);
        setStudentError(e?.message || "Unable to load your student profile.");
      } finally {
        if (!cancelled) setStudentLoading(false);
      }
    };

    fetchStudent();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, profile?.studentId]);

  const onLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <StudentContext.Provider value={{ student, setStudent, profile, studentLoading, studentError }}>
      <div className="min-h-screen bg-slate-50">
        {/* Mobile header */}
        <div className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="px-4 h-16 flex items-center justify-between">
            <Link to="/student/dashboard" className="flex items-center gap-2">
              <BrandLogo size="sm" sub={false} />
            </Link>
            <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-slate-100">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar */}
          <aside className={`${open ? "block" : "hidden"} lg:block w-64 bg-white border-r border-slate-200 lg:h-screen lg:fixed lg:left-0 lg:top-0 z-30 relative`}>
            <div className="hidden lg:flex items-center px-5 h-20 border-b border-slate-200">
              <BrandLogo size="sm" sub={false} />
            </div>

            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`student-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? "bg-blue-50 text-blue-700 font-semibold"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                  {student?.fullName?.[0] || profile?.email?.[0]?.toUpperCase() || "S"}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{student?.fullName || "Student"}</div>
                  <div className="text-xs text-slate-500 truncate">{profile?.email}</div>
                </div>
              </div>
              <button onClick={onLogout} data-testid="student-logout-btn" className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-50 border border-slate-200">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </aside>

          <main className="flex-1 lg:ml-64 min-h-screen">
            <div className="p-6 lg:p-10">
              {studentLoading ? (
                <StudentLoading />
              ) : studentError ? (
                <StudentDataIssue message={studentError} />
              ) : (
                <Outlet />
              )}
            </div>
          </main>
        </div>
      </div>
    </StudentContext.Provider>
  );
};

const StudentLoading = () => (
  <div className="text-center py-20" data-testid="student-layout-loading">
    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
    <p className="mt-4 text-slate-500">Loading your dashboard...</p>
  </div>
);

const StudentDataIssue = ({ message }) => (
  <div className="max-w-2xl mx-auto mt-16 bg-white border border-amber-200 rounded-xl p-8 text-center" data-testid="student-data-issue">
    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-4">
      <AlertCircle className="w-6 h-6" />
    </div>
    <h1 className="font-heading text-2xl font-bold text-slate-900">Student data not available</h1>
    <p className="mt-3 text-slate-600">{message}</p>
    <Link to="/register" className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold">
      Complete Registration
    </Link>
  </div>
);

export default StudentLayout;
